import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { useAlert } from '@/components/AlertProvider';
import { Settings as SettingsIcon, Eye, EyeOff, Save, User, Lock as LockIcon, CreditCard, Building2, Map } from 'lucide-react';
import { api } from '@/services/api';
import { PhoneInput } from '@/components/ui/PhoneInput';

interface UserData {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  mobile_phone: string;
  country?: string;
  currency?: string;
  role?: string;
  company_name?: string;
  siret?: string;
  address?: string;
  city?: string;
  postal_code?: string;
  maps_link?: string;
  bank_name?: string;
  bank_account_holder?: string;
  iban?: string;
  bic?: string;
}

interface RibSettings {
  account_holder: string;
  address_line1: string;
  address_line2: string;
  domiciliation: string;
  bank_code: string;
  counter_code: string;
  account_number: string;
  rib_key: string;
  iban: string;
  bic: string;
}

export default function Settings() {
  const { showSuccess, showError, showInfo } = useAlert();
  const { logout, setUser, user } = useAuthStore();
  const navigate = useNavigate();

  const buildAddressComplete = (address: string, postalCode: string, city: string, country?: string) => {
    return [address, postalCode, city, country]
      .map((v) => (typeof v === 'string' ? v.trim() : ''))
      .filter(Boolean)
      .join(' ');
  };

  const buildMapsLink = (addressComplete: string) => {
    const trimmed = addressComplete.trim();
    return trimmed ? `https://www.google.com/maps/search/${encodeURIComponent(trimmed)}` : '';
  };

  const buildMapsEmbedSrc = (addressComplete: string) => {
    const trimmed = addressComplete.trim();
    return trimmed
      ? `https://www.google.com/maps?q=${encodeURIComponent(trimmed)}&output=embed`
      : '';
  };

  // User profile state
  const [userData, setUserData] = useState<UserData>({
    id: 0,
    first_name: '',
    last_name: '',
    email: '',
    mobile_phone: '',
    country: 'France',
    currency: '€',
    role: 'user',
    company_name: '',
    siret: '',
    address: '',
    city: '',
    postal_code: '',
    maps_link: '',
    bank_name: '',
    bank_account_holder: '',
    iban: '',
    bic: '',
  });

  const [showCompanyMapPreview, setShowCompanyMapPreview] = useState(false);
  const [ribSettings, setRibSettings] = useState<RibSettings>({
    account_holder: '',
    address_line1: '',
    address_line2: '',
    domiciliation: '',
    bank_code: '',
    counter_code: '',
    account_number: '',
    rib_key: '',
    iban: '',
    bic: ''
  });

  const [loading, setLoading] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [activeTab, setActiveTab] = useState<'profile' | 'company' | 'bank' | 'security' | 'billing'>('profile');

  const currencyOptions: Array<{ code: string; label: string }> = [
    { code: '€', label: ' €' },
    { code: '$', label: 'USD ($)' },
    { code: '£', label: 'GBP (£)' },
    { code: 'CHF', label: 'CHF (CHF)' },
    { code: 'CAD', label: 'CAD ($)' },
    { code: 'AUD', label: 'AUD ($)' },
    { code: 'JPY', label: 'JPY (¥)' },
    { code: 'CNY', label: 'CNY (¥)' },
    { code: 'INR', label: 'INR (₹)' },
  ];

  const countryOptions: string[] = [
    'France',
    'Belgique',
    'Suisse',
    'Luxembourg',
    'Espagne',
    'Allemagne',
    'Italie',
    'Royaume-Uni',
    'USA',
    'Canada',
    'Portugal',
    'Pays-Bas',
    'Irlande',
    'Autriche',
    'Pologne',
    'Suède',
    'Norvège',
    'Danemark',
    'Finlande',
    'Grèce',
    'Australie',
    'Nouvelle-Zélande',
    'Japon',
    'Chine',
    'Inde',
    'Brésil',
    'Mexique',
  ].sort();

  // Password state (separate from profile)
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [awaitingPasswordOtp, setAwaitingPasswordOtp] = useState(false);
  const [passwordOtpCode, setPasswordOtpCode] = useState('');
  const [otpModalOpen, setOtpModalOpen] = useState(false);
  const [resendCooldownSeconds, setResendCooldownSeconds] = useState(0);
  const [showPasswords, setShowPasswords] = useState({ current: false, new: false, confirm: false });

  useEffect(() => {
    loadCurrentUser();
  }, []);

  useEffect(() => {
    if (activeTab === 'billing' && userData.role === 'super_admin') {
      loadRibSettings();
    }
  }, [activeTab, userData.role]);

  useEffect(() => {
    if (resendCooldownSeconds <= 0) return;
    const id = window.setInterval(() => {
      setResendCooldownSeconds((s) => Math.max(0, s - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [resendCooldownSeconds]);

  const loadCurrentUser = async () => {
    try {
      setLoadingProfile(true);
      const response = await api.get('/users/me');
      setUserData({
        id: response.data.id,
        first_name: response.data.first_name || '',
        last_name: response.data.last_name || '',
        email: response.data.email || '',
        mobile_phone: response.data.mobile_phone || '',
        country: response.data.country || 'France',
        currency: response.data.currency || '€',
        role: response.data.role || 'user',
        company_name: response.data.company_name || '',
        siret: response.data.siret || '',
        address: response.data.address || '',
        city: response.data.city || '',
        postal_code: response.data.postal_code || '',
        maps_link: response.data.maps_link || '',
        bank_name: response.data.bank_name || '',
        bank_account_holder: response.data.bank_account_holder || '',
        iban: response.data.iban || '',
        bic: response.data.bic || '',
      });
    } catch (error) {
      console.error('Error loading user:', error);
      showError('Impossible de charger vos données');
    } finally {
      setLoadingProfile(false);
    }
  };

  const loadRibSettings = async () => {
    try {
      const response = await api.get('/organization/rib');
      if (response.data) {
        setRibSettings(response.data);
      }
    } catch (error) {
      console.error('Error loading rib settings:', error);
    }
  };

  const handleSaveProfile = async () => {
    setLoading(true);
    try {
      const addressParts = [userData.address, userData.postal_code, userData.city, userData.country]
        .map((v) => (typeof v === 'string' ? v.trim() : ''))
        .filter(Boolean)
        .join(' ');
      const maps_link = (userData.maps_link && userData.maps_link.trim())
        ? userData.maps_link.trim()
        : (addressParts ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addressParts)}` : undefined);

      await api.put('/users/me', {
        first_name: userData.first_name,
        last_name: userData.last_name,
        email: userData.email,
        mobile_phone: userData.mobile_phone,
        country: userData.country || 'France',
        currency: userData.currency || '€',
        company_name: userData.company_name || undefined,
        siret: userData.siret || undefined,
        address: userData.address || undefined,
        city: userData.city || undefined,
        postal_code: userData.postal_code || undefined,
        maps_link,
        bank_name: userData.bank_name || undefined,
        bank_account_holder: userData.bank_account_holder || undefined,
        iban: userData.iban || undefined,
        bic: userData.bic || undefined,
      });

      // Update local auth state to reflect changes in Header
      if (user) {
        setUser({
          ...user,
          first_name: userData.first_name,
          last_name: userData.last_name,
          email: userData.email
        });
      }

      showSuccess('Profil mis à jour avec succès');
    } catch (error: any) {
      showError(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveRibSettings = async () => {
    setLoading(true);
    try {
      await api.post('/organization/rib', ribSettings);
      showSuccess('Paramètres de facturation mis à jour');
    } catch (error: any) {
      showError(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      showError('Veuillez remplir tous les champs');
      return;
    }

    if (newPassword !== confirmPassword) {
      showError('Les mots de passe ne correspondent pas');
      return;
    }

    if (newPassword.length < 6) {
      showError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/users/me/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
        otp_code: awaitingPasswordOtp ? passwordOtpCode : undefined
      });

      if (response?.data?.requires_otp) {
        setAwaitingPasswordOtp(true);
        setOtpModalOpen(true);
        setResendCooldownSeconds(30);
        showInfo('Un code a été envoyé par email. Saisissez-le pour confirmer.');
        setLoading(false);
        return;
      }

      showSuccess('Mot de passe mis à jour. Déconnexion...');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordOtpCode('');
      setAwaitingPasswordOtp(false);
      setOtpModalOpen(false);
      setResendCooldownSeconds(0);
      setTimeout(() => { logout(); navigate('/login'); }, 1500);
    } catch (error: any) {
      showError(error.response?.data?.detail || 'Erreur lors du changement de mot de passe');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmOtp = async () => {
    if (passwordOtpCode.length !== 6) {
      showError('Veuillez saisir un code à 6 chiffres');
      return;
    }

    setLoading(true);
    try {
      await api.post('/users/me/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
        otp_code: passwordOtpCode
      });

      showSuccess('Mot de passe mis à jour. Déconnexion...');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordOtpCode('');
      setAwaitingPasswordOtp(false);
      setOtpModalOpen(false);
      setResendCooldownSeconds(0);
      setTimeout(() => { logout(); navigate('/login'); }, 1500);
    } catch (error: any) {
      showError(error.response?.data?.detail || 'Code incorrect');
    } finally {
      setLoading(false);
    }
  };


  const handleResendPasswordOtp = async () => {
    if (!currentPassword || !newPassword) {
      showError('Veuillez remplir tous les champs');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/users/me/change-password', {
        current_password: currentPassword,
        new_password: newPassword
      });

      if (response?.data?.requires_otp) {
        setAwaitingPasswordOtp(true);
        setOtpModalOpen(true);
        setResendCooldownSeconds(30);
        showInfo('Un nouveau code a été envoyé par email.');
        return;
      }

      showInfo('Le changement de mot de passe a été traité.');
    } catch (error: any) {
      showError(error.response?.data?.detail || 'Erreur lors de l\'envoi du code');
    } finally {
      setLoading(false);
    }
  };

  if (loadingProfile) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-full p-2 sm:p-4 pb-24 bg-slate-50 dark:bg-slate-900">
      <div className="w-full max-w-[46rem] mx-auto bg-white dark:bg-slate-800 rounded-xl shadow-lg p-4 sm:p-6">
        {/* Header */}
        <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
          <div className="p-1.5 sm:p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
            <SettingsIcon className="h-5 w-5 sm:h-6 sm:w-6 text-purple-600 dark:text-purple-400" />
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <h1 className="text-base sm:text-xl font-bold text-slate-800 dark:text-slate-100">
              Mon Compte <span className="text-[10px] sm:text-xs font-normal text-slate-400">({userData.role})</span>
            </h1>
            {(userData.role === 'admin' || userData.role === 'super_admin' || userData.role === 'apporteur') && (
              <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                userData.role === 'super_admin'
                  ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                  : userData.role === 'admin'
                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                    : 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                }`}>
                {userData.role === 'super_admin' ? 'Super Admin' : userData.role === 'admin' ? 'Admin' : 'Apporteur'}
              </span>
            )}
          </div>
        </div>

        {/* Tabs — same underline style, shorter labels on mobile to fit one row */}
        <div className="flex gap-0 mb-4 sm:mb-6 border-b border-slate-200 dark:border-slate-700 overflow-x-auto">
          <button
            onClick={() => setActiveTab('profile')}
            className={`flex shrink-0 items-center gap-1.5 px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium transition-colors border-b-2 whitespace-nowrap ${activeTab === 'profile'
              ? 'border-purple-600 text-purple-600 dark:text-purple-400'
              : 'border-transparent text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
              }`}
          >
            <User className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            Infos Utilisateur
          </button>
          {userData.role === 'apporteur' && (
            <button
              onClick={() => setActiveTab('company')}
              className={`flex shrink-0 items-center gap-1.5 px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium transition-colors border-b-2 whitespace-nowrap ${activeTab === 'company'
                ? 'border-purple-600 text-purple-600 dark:text-purple-400'
                : 'border-transparent text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
                }`}
            >
              <Building2 className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              Entreprise
            </button>
          )}
          {userData.role === 'apporteur' && (
            <button
              onClick={() => setActiveTab('bank')}
              className={`flex shrink-0 items-center gap-1.5 px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium transition-colors border-b-2 whitespace-nowrap ${activeTab === 'bank'
                ? 'border-purple-600 text-purple-600 dark:text-purple-400'
                : 'border-transparent text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
                }`}
            >
              <CreditCard className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              Banque
            </button>
          )}
          <button
            onClick={() => setActiveTab('security')}
            className={`flex shrink-0 items-center gap-1.5 px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium transition-colors border-b-2 whitespace-nowrap ${activeTab === 'security'
              ? 'border-purple-600 text-purple-600 dark:text-purple-400'
              : 'border-transparent text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
              }`}
          >
            <LockIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            Sécurité
          </button>
          {userData.role === 'super_admin' && (
            <button
              onClick={() => setActiveTab('billing')}
              className={`flex shrink-0 items-center gap-1.5 px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium transition-colors border-b-2 whitespace-nowrap ${activeTab === 'billing'
                ? 'border-purple-600 text-purple-600 dark:text-purple-400'
                : 'border-transparent text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
                }`}
            >
              <CreditCard className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              RIB / Banque
            </button>
          )}
        </div>

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Prénom */}
              <div>
                <label htmlFor="first_name" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Prénom</label>
                <input
                  id="first_name"
                  type="text"
                  autoComplete="off"
                  value={userData.first_name}
                  onChange={(e) => setUserData({ ...userData, first_name: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              {/* Nom */}
              <div>
                <label htmlFor="last_name" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Nom</label>
                <input
                  id="last_name"
                  type="text"
                  autoComplete="off"
                  value={userData.last_name}
                  onChange={(e) => setUserData({ ...userData, last_name: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Email</label>
                <input
                  id="email"
                  type="email"
                  autoComplete="off"
                  value={userData.email}
                  onChange={(e) => setUserData({ ...userData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              {/* Téléphone */}
              <div>
                <label htmlFor="mobile_phone" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Téléphone</label>
                <PhoneInput
                  value={userData.mobile_phone}
                  onChange={(value) => setUserData({ ...userData, mobile_phone: value })}
                />
              </div>

              {/* Devise */}
              <div>
                <label htmlFor="currency" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Devise</label>
                <select
                  id="currency"
                  autoComplete="off"
                  value={userData.currency || '€'}
                  onChange={(e) => setUserData({ ...userData, currency: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  {currencyOptions.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Pays */}
              <div>
                <label htmlFor="country" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Pays</label>
                <select
                  id="country"
                  autoComplete="off"
                  value={userData.country || 'France'}
                  onChange={(e) => setUserData({ ...userData, country: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  {countryOptions.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end">
              <button
                onClick={handleSaveProfile}
                disabled={loading}
                className="flex items-center gap-2 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
              >
                <Save className="h-4 w-4" />
                {loading ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </div>
        )}

        {activeTab === 'company' && userData.role === 'apporteur' && (
          <div className="space-y-6">
            <div>
              {/* <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-4 flex items-center gap-2">
                <Building2 className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                Informations société
              </h3> */}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label htmlFor="company_name" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Nom de la société</label>
                  <input
                    id="company_name"
                    type="text"
                    autoComplete="off"
                    value={userData.company_name || ''}
                    onChange={(e) => setUserData((prev) => ({ ...prev, company_name: e.target.value }))}
                    placeholder="Ex: Ma Société SARL"
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                <div className="sm:col-span-2">
                  <label htmlFor="siret" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">SIRET</label>
                  <input
                    id="siret"
                    type="text"
                    autoComplete="off"
                    value={userData.siret || ''}
                    onChange={(e) => setUserData((prev) => ({ ...prev, siret: e.target.value }))}
                    placeholder="Ex: 123 456 789 00012"
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                <div className="sm:col-span-2">
                  <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-3">Localisation</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="sm:col-span-2">
                        <label htmlFor="company_address" className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">Rue</label>
                        <input
                          id="company_address"
                          type="text"
                          autoComplete="off"
                          value={userData.address || ''}
                          onChange={(e) =>
                            setUserData((prev) => {
                              const next = { ...prev, address: e.target.value };
                              const addressComplete = buildAddressComplete(next.address || '', next.postal_code || '', next.city || '', next.country);
                              return { ...next, maps_link: buildMapsLink(addressComplete) };
                            })
                          }
                          placeholder="Ex: 44 Avenue Gambetta"
                          className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label htmlFor="company_postal_code" className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">Code postal</label>
                        <input
                          id="company_postal_code"
                          type="text"
                          autoComplete="off"
                          value={userData.postal_code || ''}
                          onChange={(e) =>
                            setUserData((prev) => {
                              const next = { ...prev, postal_code: e.target.value };
                              const addressComplete = buildAddressComplete(next.address || '', next.postal_code || '', next.city || '', next.country);
                              return { ...next, maps_link: buildMapsLink(addressComplete) };
                            })
                          }
                          placeholder="Ex: 17100"
                          className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label htmlFor="company_city" className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">Ville</label>
                        <input
                          id="company_city"
                          type="text"
                          autoComplete="off"
                          value={userData.city || ''}
                          onChange={(e) =>
                            setUserData((prev) => {
                              const next = { ...prev, city: e.target.value };
                              const addressComplete = buildAddressComplete(next.address || '', next.postal_code || '', next.city || '', next.country);
                              return { ...next, maps_link: buildMapsLink(addressComplete) };
                            })
                          }
                          placeholder="Ex: Saintes"
                          className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        />
                      </div>

                      <div className="sm:col-span-2">
                        <label htmlFor="company_country" className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">Pays</label>
                        <select
                          id="company_country"
                          autoComplete="off"
                          value={userData.country || 'France'}
                          onChange={(e) =>
                            setUserData((prev) => {
                              const next = { ...prev, country: e.target.value };
                              const addressComplete = buildAddressComplete(next.address || '', next.postal_code || '', next.city || '', next.country);
                              return { ...next, maps_link: buildMapsLink(addressComplete) };
                            })
                          }
                          className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        >
                          {countryOptions.map((name) => (
                            <option key={name} value={name}>
                              {name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="sm:col-span-2">
                        <label htmlFor="company_address_complete" className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">Adresse complète</label>
                        <input
                          id="company_address_complete"
                          type="text"
                          readOnly
                          value={buildAddressComplete(userData.address || '', userData.postal_code || '', userData.city || '', userData.country)}
                          className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-slate-50 dark:bg-slate-800/60 text-slate-900 dark:text-slate-100"
                        />

                        {buildAddressComplete(userData.address || '', userData.postal_code || '', userData.city || '', userData.country) && (
                          <div className="mt-2 flex flex-wrap items-center gap-3">
                            <button
                              type="button"
                              onClick={() => setShowCompanyMapPreview((v) => !v)}
                              className={
                                "inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium transition-colors " +
                                (showCompanyMapPreview
                                  ? 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900/40 dark:bg-blue-950/40 dark:text-blue-300'
                                  : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700')
                              }
                            >
                              <Map className="h-3.5 w-3.5" />
                              Carte
                            </button>
                            {userData.maps_link && (
                              <a
                                href={userData.maps_link}
                                target="_blank"
                                rel="noreferrer"
                                className="text-sm text-blue-600 hover:underline hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                              >
                                Ouvrir dans Google Maps
                              </a>
                            )}
                          </div>
                        )}

                        {showCompanyMapPreview && (
                          <div className="mt-3 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                            <iframe
                              title="Google Maps"
                              src={buildMapsEmbedSrc(buildAddressComplete(userData.address || '', userData.postal_code || '', userData.city || '', userData.country))}
                              className="w-full h-64"
                              referrerPolicy="no-referrer-when-downgrade"
                              allowFullScreen
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                onClick={handleSaveProfile}
                disabled={loading}
                className="flex items-center gap-2 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
              >
                <Save className="h-4 w-4" />
                {loading ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </div>
        )}

        {activeTab === 'bank' && userData.role === 'apporteur' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-4 flex items-center gap-2">
                <CreditCard className="h-4 w-4 text-emerald-600" />
                Informations bancaires
              </h3>

              <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="bank_name" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Banque</label>
                    <input
                      id="bank_name"
                      type="text"
                      autoComplete="off"
                      value={userData.bank_name || ''}
                      onChange={(e) => setUserData((prev) => ({ ...prev, bank_name: e.target.value }))}
                      placeholder="Nom de la banque"
                      className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label htmlFor="bank_account_holder" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Titulaire du compte</label>
                    <input
                      id="bank_account_holder"
                      type="text"
                      autoComplete="off"
                      value={userData.bank_account_holder || ''}
                      onChange={(e) => setUserData((prev) => ({ ...prev, bank_account_holder: e.target.value }))}
                      placeholder="Nom du titulaire"
                      className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label htmlFor="iban" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">IBAN</label>
                    <input
                      id="iban"
                      type="text"
                      autoComplete="off"
                      value={userData.iban || ''}
                      onChange={(e) => setUserData((prev) => ({ ...prev, iban: e.target.value }))}
                      placeholder="FR76 3000 4000 5000 6000 7000 899"
                      className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label htmlFor="bic" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">BIC / SWIFT</label>
                    <input
                      id="bic"
                      type="text"
                      autoComplete="off"
                      value={userData.bic || ''}
                      onChange={(e) => setUserData((prev) => ({ ...prev, bic: e.target.value }))}
                      placeholder="Ex : PSSTFRPPXXX"
                      className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                onClick={handleSaveProfile}
                disabled={loading}
                className="flex items-center gap-2 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
              >
                <Save className="h-4 w-4" />
                {loading ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </div>
        )}

        {/* Security Tab */}
        {activeTab === 'security' && (
          <div className="space-y-6">
            <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <p className="text-sm text-blue-800 dark:text-blue-300">
                Pour des raisons de sécurité, un code de vérification sera envoyé à votre email lors du changement de mot de passe.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4">
              {/* Mot de passe actuel */}
              <div>
                <label htmlFor="current_password" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Mot de passe actuel</label>
                <div className="relative">
                  <input
                    id="current_password"
                    type={showPasswords.current ? 'text' : 'password'}
                    autoComplete="current-password"
                    value={currentPassword}
                    onChange={(e) => {
                      setCurrentPassword(e.target.value);
                      if (awaitingPasswordOtp) {
                        setAwaitingPasswordOtp(false);
                        setPasswordOtpCode('');
                      }
                    }}
                    placeholder="••••••••"
                    className="w-full px-3 py-2 pr-10 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPasswords.current ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {/* Nouveau mot de passe */}
              <div>
                <label htmlFor="new_password" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Nouveau mot de passe</label>
                <div className="relative">
                  <input
                    id="new_password"
                    type={showPasswords.new ? 'text' : 'password'}
                    autoComplete="new-password"
                    value={newPassword}
                    onChange={(e) => {
                      setNewPassword(e.target.value);
                      if (awaitingPasswordOtp) {
                        setAwaitingPasswordOtp(false);
                        setPasswordOtpCode('');
                      }
                    }}
                    placeholder="••••••••"
                    className="w-full px-3 py-2 pr-10 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPasswords.new ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {/* Confirmer mot de passe */}
              <div>
                <label htmlFor="confirm_password" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Confirmer le mot de passe</label>
                <div className="relative">
                  <input
                    id="confirm_password"
                    type={showPasswords.confirm ? 'text' : 'password'}
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      if (awaitingPasswordOtp) {
                        setAwaitingPasswordOtp(false);
                        setPasswordOtpCode('');
                      }
                    }}
                    placeholder="••••••••"
                    className="w-full px-3 py-2 pr-10 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPasswords.confirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end">
              <button
                onClick={handleChangePassword}
                disabled={loading || !currentPassword || !newPassword || !confirmPassword}
                className="flex items-center gap-2 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
              >
                <LockIcon className="h-4 w-4" />
                {loading ? 'Changement...' : 'Changer le mot de passe'}
              </button>
            </div>
          </div>
        )}

        {/* Billing Tab (Super Admin) */}
        {activeTab === 'billing' && userData.role === 'super_admin' && (
          <div className="space-y-6">
            <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <p className="text-sm text-blue-800 dark:text-blue-300">
                Ces informations apparaîtront sur l'annexe RIB des factures générées.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label htmlFor="rib_account_holder" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Titulaire du compte</label>
                <input
                  id="rib_account_holder"
                  type="text"
                  value={ribSettings.account_holder}
                  onChange={(e) => setRibSettings({ ...ribSettings, account_holder: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              <div className="md:col-span-2">
                <label htmlFor="rib_address_line1" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Adresse</label>
                <input
                  id="rib_address_line1"
                  type="text"
                  value={ribSettings.address_line1}
                  onChange={(e) => setRibSettings({ ...ribSettings, address_line1: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              <div className="md:col-span-2">
                <label htmlFor="rib_address_line2" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Code Postal & Ville</label>
                <input
                  id="rib_address_line2"
                  type="text"
                  value={ribSettings.address_line2}
                  onChange={(e) => setRibSettings({ ...ribSettings, address_line2: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              <div className="md:col-span-2">
                <label htmlFor="rib_domiciliation" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Domiciliation</label>
                <input
                  id="rib_domiciliation"
                  type="text"
                  value={ribSettings.domiciliation}
                  onChange={(e) => setRibSettings({ ...ribSettings, domiciliation: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              {/* Codes RIB - Layout optimisé */}
              <div className="md:col-span-2 p-4 bg-slate-50 dark:bg-slate-900/50 rounded-lg border border-slate-200 dark:border-slate-700">
                <div className="flex flex-wrap md:flex-nowrap gap-4">
                  <div className="w-full md:w-32">
                    <label htmlFor="rib_bank_code" className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1 uppercase">Code Banque</label>
                    <input
                      id="rib_bank_code"
                      type="text"
                      maxLength={5}
                      value={ribSettings.bank_code}
                      onChange={(e) => setRibSettings({ ...ribSettings, bank_code: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent text-center font-mono"
                    />
                  </div>

                  <div className="w-full md:w-32">
                    <label htmlFor="rib_counter_code" className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1 uppercase">Code Guichet</label>
                    <input
                      id="rib_counter_code"
                      type="text"
                      maxLength={5}
                      value={ribSettings.counter_code}
                      onChange={(e) => setRibSettings({ ...ribSettings, counter_code: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent text-center font-mono"
                    />
                  </div>

                  <div className="w-full md:flex-1">
                    <label htmlFor="rib_account_number" className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1 uppercase">N° Compte</label>
                    <input
                      id="rib_account_number"
                      type="text"
                      maxLength={11}
                      value={ribSettings.account_number}
                      onChange={(e) => setRibSettings({ ...ribSettings, account_number: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent text-center font-mono"
                    />
                  </div>

                  <div className="w-full md:w-20">
                    <label htmlFor="rib_key" className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1 uppercase">Clé</label>
                    <input
                      id="rib_key"
                      type="text"
                      maxLength={2}
                      value={ribSettings.rib_key}
                      onChange={(e) => setRibSettings({ ...ribSettings, rib_key: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent text-center font-mono"
                    />
                  </div>
                </div>
              </div>

              <div className="md:col-span-2">
                <label htmlFor="rib_iban" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">IBAN</label>
                <input
                  id="rib_iban"
                  type="text"
                  value={ribSettings.iban}
                  onChange={(e) => setRibSettings({ ...ribSettings, iban: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              <div className="md:col-span-2">
                <label htmlFor="rib_bic" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">BIC (SWIFT)</label>
                <input
                  id="rib_bic"
                  type="text"
                  value={ribSettings.bic}
                  onChange={(e) => setRibSettings({ ...ribSettings, bic: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="flex justify-end">
              <button
                onClick={handleSaveRibSettings}
                disabled={loading}
                className="flex items-center gap-2 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
              >
                <Save className="h-4 w-4" />
                {loading ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </div>
        )}

        {otpModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-md rounded-xl bg-white dark:bg-slate-800 shadow-lg p-6">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Vérification</h2>
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                Un code a été envoyé par email. Entrez le code pour confirmer le changement de mot de passe.
              </p>

              <div className="mt-4">
                <label htmlFor="password_otp" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Code (6 chiffres)</label>
                <input
                  id="password_otp"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={passwordOtpCode}
                  onChange={(e) => setPasswordOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="123456"
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              <div className="mt-4 flex items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setOtpModalOpen(false);
                  }}
                  className="px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
                >
                  Annuler
                </button>

                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={handleResendPasswordOtp}
                    disabled={loading || resendCooldownSeconds > 0}
                    className="px-4 py-2 rounded-lg bg-slate-100 dark:bg-slate-700 text-slate-800 dark:text-slate-100 hover:bg-slate-200 dark:hover:bg-slate-600 disabled:opacity-50"
                  >
                    {resendCooldownSeconds > 0 ? `Renvoyer (${resendCooldownSeconds}s)` : 'Renvoyer'}
                  </button>

                  <button
                    type="button"
                    onClick={handleConfirmOtp}
                    disabled={loading || passwordOtpCode.length !== 6}
                    className="px-4 py-2 rounded-lg bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50"
                  >
                    Confirmer
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
